<template>
	<div class="container mx-auto max-w-5xl px-4">
		<div class="flex flex-col justify-between">
			<div class="xs:p-2 flex overflow-hidden p-0">
				<HelpfulTips>
					Please choose an option that best describes your {{ form.make }}. There is no
					exact science to it, so choose what fits best or let us know if you are not
					sure.
				</HelpfulTips>

				<div class="grow basis-1/2 overflow-y-auto px-4">
					<div
						class="mb-10 box-border flex cursor-pointer flex-col lg:mb-4"
						@click="() => handleRateConditionChange(RateCondition.Clean)"
					>
						<div class="flex">
							<div class="xs:mr-6 mr-3">
								<AppCheckbox
									class="text-success-base mt-1"
									:checked="form.rateCondition === RateCondition.Clean"
								/>
							</div>

							<div class="flex flex-col">
								<div class="flex items-center justify-between">
									<div
										class="text-main-400 flex grow justify-between text-base font-bold lg:text-xl"
									>
										<p>Clean</p>
										<div class="flex">
											<StarSvg full class="fill-success-base ml-3" />
											<StarSvg full class="fill-success-base" />
											<StarSvg full class="fill-success-base" />
											<StarSvg full class="fill-success-base" />
										</div>
									</div>
								</div>

								<div class="text-15 xs:text-base lg:text-xl">
									My car is like new. It needs no reconditioning to be resold. It
									has no mechanical issues, cosmetic repairs, bad history, and no
									title or odometer issues. My ride comes with all original
									equipment, all keys, books and manuals and has all service
									records.
								</div>
							</div>
						</div>
					</div>

					<div
						class="mb-10 box-border flex cursor-pointer flex-col lg:mb-4"
						@click="() => handleRateConditionChange(RateCondition.Average)"
					>
						<div class="flex">
							<div class="xs:mr-6 mr-3">
								<AppCheckbox
									class="text-success-base mt-1"
									:checked="form.rateCondition === RateCondition.Average"
								/>
							</div>

							<div class="flex flex-col">
								<div class="flex items-center justify-between">
									<div
										class="text-main-400 flex grow justify-between text-base font-bold lg:text-xl"
									>
										<p>Average</p>
										<div class="flex">
											<StarSvg full class="fill-success-base ml-3" />
											<StarSvg full class="fill-success-base" />
											<StarSvg full class="fill-success-base" />
											<StarSvg class="fill-success-base" />
										</div>
									</div>
								</div>

								<div class="text-15 xs:text-base lg:text-xl">
									My car has normal signs of wear and tear and needs minor
									reconditioning to be resold. It may have small dents, dings or
									scratches and may need some maintenance, but does not require
									major mechanical or cosmetic repair. There are no title, history
									or odometer issues.
								</div>
							</div>
						</div>
					</div>

					<div
						class="mb-10 box-border flex cursor-pointer flex-col lg:mb-4"
						@click="() => handleRateConditionChange(RateCondition.Rough)"
					>
						<div class="flex">
							<div class="xs:mr-6 mr-3">
								<AppCheckbox
									class="text-warning mt-1"
									:checked="form.rateCondition === RateCondition.Rough"
								/>
							</div>

							<div class="flex flex-col">
								<div class="flex items-center justify-between">
									<div
										class="text-main-400 flex grow justify-between text-base font-bold lg:text-xl"
									>
										<p>Rough</p>
										<div class="flex">
											<StarSvg full class="fill-warning ml-3" />
											<StarSvg full class="fill-warning" />
											<StarSvg class="fill-warning" />
											<StarSvg class="fill-warning" />
										</div>
									</div>
								</div>

								<div class="text-15 xs:text-base lg:text-xl">
									My car has major but repairable mechanical or cosmetic issues
									and may need significant reconditioning to be resold. No title,
									history or odometer issues.
								</div>
							</div>
						</div>
					</div>

					<div
						class="mb-10 box-border flex cursor-pointer flex-col lg:mb-4"
						@click="() => handleRateConditionChange(RateCondition.Damaged)"
					>
						<div class="flex">
							<div class="xs:mr-6 mr-3">
								<AppCheckbox
									class="text-warning mt-1"
									:checked="form.rateCondition === RateCondition.Damaged"
								/>
							</div>

							<div class="flex flex-col">
								<div class="flex items-center justify-between">
									<div
										class="text-main-400 flex grow justify-between text-base font-bold lg:text-xl"
									>
										<p>Damaged</p>
										<div class="flex">
											<StarSvg full class="fill-warning ml-3" />
											<StarSvg class="fill-warning" />
											<StarSvg class="fill-warning" />
											<StarSvg class="fill-warning" />
										</div>
									</div>
								</div>

								<div class="text-15 xs:text-base lg:text-xl">
									May car has irreparable damage or title and history issues. Some
									of these issues are: odometer rollback, hail, water or frame
									damage, lemon or manufacturer buy back, non-operable condition,
									salvage, junk or other branded title.
								</div>
							</div>
						</div>
					</div>

					<div
						class="mb-10 box-border flex cursor-pointer flex-col lg:mb-4"
						@click="() => handleRateConditionChange(RateCondition.NotSure)"
					>
						<div class="flex">
							<div class="xs:mr-6 mr-3">
								<AppCheckbox
									class="text-warning mt-1"
									:checked="form.rateCondition === RateCondition.NotSure"
								/>
							</div>

							<div class="flex flex-col">
								<div class="flex items-center justify-between">
									<div
										class="text-main-400 flex grow justify-between text-base font-bold lg:text-xl"
									>
										<p>Not Sure</p>
										<div class="flex"></div>
									</div>
								</div>

								<div class="text-15 xs:text-base lg:text-xl">
									I'm not sure of what condition my ride is in. No problem! Please
									choose this option and we will ask you a couple more questions
									about your ride.
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { RateCondition, useFormStore } from '../../../stores/form'
	import StarSvg from '../components/icons/StarSvg.vue'
	import HelpfulTips from '../components/HelpfulTips.vue'
	import AppCheckbox from '../../common/AppCheckbox.vue'

	const form = useFormStore()

	const handleRateConditionChange = (condition: RateCondition) => {
		if (condition !== form.rateCondition) {
			form.rateCondition = condition
			form.mechIssueItems = []
			form.cosmIssueItems = []
		}
	}
</script>
